import React, { useRef, useState } from "react";
import Layout2 from "../../components/global/layout2";
import ApiClient from "../../methods/api/apiClient";
import { toast } from "react-toastify";

// Lane Pricing — Excel round-trip.
//
// Upload a lane pricing sheet with the columns:
//   trip# | Origin city | Origin State | Destination city | Destination State |
//   Target Rate | Status | Linehaul Rate | Difference
//
// The backend resolves each distinct origin→destination to a SONAR market,
// fetches the current market rate ONCE per distinct lane, writes it into
// "Target Rate", recomputes "Difference = Linehaul Rate − Target Rate", and
// returns the enriched workbook as a download. Everything else is untouched.

const money = (v) =>
  typeof v === "number" ? "$" + v.toLocaleString(undefined, { maximumFractionDigits: 2 }) : "—";

const sourceLabel = (s) =>
  s === "fresh" ? "Live SONAR" :
  s === "cache" ? "Cache" :
  s === "no_kma_match" ? "No match" :
  s === "no_data" ? "No data" :
  s === "auth_failed" ? "Auth failed" :
  s;

// Decode a base64 xlsx and trigger a browser download.
const downloadBase64 = (base64, filename, mime) => {
  try {
    const byteChars = atob(base64);
    const bytes = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
    const blob = new Blob([bytes], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    toast.error("Could not build the download: " + (e && e.message ? e.message : e));
  }
};

export default function LanePricing() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;

    setBusy(true);
    setResult(null);
    // Bulk resolution (SONAR + geocode fallback per distinct lane) can run
    // well past a typical upload's timeout on a sheet with many lanes.
    const res = await ApiClient.postFormData("lane-pricing/target-rates", { file }, 10 * 60 * 1000);
    setBusy(false);

    if (res && res.success) {
      setResult(res);
      toast.success("Target rates filled — download started");
      downloadBase64(res.base64, res.filename, res.mimeType);
    } else {
      toast.error((res && res.error && res.error.message) || "Failed to process the file");
    }
  };

  const s = result && result.summary;

  return (
    <Layout2 title="Lane Pricing">
      <div className="mainareas">
        <div className="edit-page-common edit-page-commons">
          <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
            <h3 className="ViewUser mb-0">
              <i className="fa fa-file-excel-o me-2"></i>
              Lane Pricing
            </h3>
          </div>

          <div className="white-bg-main" style={{ padding: 20 }}>
            {/* Instructions */}
            <div style={{ maxWidth: 820, marginBottom: 20, fontSize: 14, color: "#5a6b7b" }}>
              <p className="mb-1">
                Upload a lane pricing Excel. Repeated lanes are looked up <b>once</b> — no duplicate
                SONAR calls. For every row we fill <b>Target Rate</b> with the current SONAR market
                average, add <b>Lowest Rate</b> / <b>Highest Rate</b> (SONAR's range around that
                average), and compute <b>Difference = Linehaul Rate − Target Rate</b>. Nothing else in
                the sheet is changed.
              </p>
              <p className="mb-0" style={{ fontSize: 12 }}>
                Accepted columns: <b>trip#</b>, <b>Origin city</b>, <b>Origin State</b>,
                <b> Destination city</b>, <b>Destination State</b>, <b>Target Rate</b>, <b>Status</b>,
                <b> Linehaul Rate</b>, <b>Difference</b> — or a compact <b>Origin</b> /{" "}
                <b>Destination</b> header (city in that column, state in the next). No ZIP codes
                needed — city/state is resolved to a market automatically.
              </p>
            </div>

            {/* Upload */}
            <div className="mb-4">
              <label className="btn btn-primary cursor-pointer mb-0">
                {busy ? (
                  <span>
                    <i className="fa fa-spinner fa-spin me-2"></i>Processing…
                  </span>
                ) : (
                  <span>
                    <i className="fa fa-upload me-2"></i>Upload pricing Excel
                  </span>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  className="hidden"
                  accept=".xlsx,.xlsm,.xls"
                  disabled={busy}
                  onChange={onFile}
                />
              </label>
            </div>

            {/* Result summary */}
            {s && (
              <div>
                <div
                  className="mb-3 d-flex flex-wrap align-items-center gap-3"
                  style={{
                    background: "#f0fdf4", borderRadius: 8, padding: "12px 16px",
                    border: "1px solid #bce5c9",
                  }}
                >
                  <div style={{ fontWeight: 600 }}>
                    {s.filled.toLocaleString()} of {s.totalRows.toLocaleString()} rows filled
                  </div>
                  <div className="small" style={{ color: "#198754" }}>
                    {s.uniqueLanes.toLocaleString()} distinct lanes
                  </div>
                  <div className="small" style={{ color: "#0d6efd" }}>
                    {s.liveCalls} live · {s.cacheHits} cached
                  </div>
                  {s.noKma > 0 && (
                    <div className="small" style={{ color: "#dc3545" }}>
                      {s.noKma} no market match
                    </div>
                  )}
                  <div className="ms-auto">
                    <button
                      className="btn btn-sm btn-outline-success"
                      onClick={() => result && downloadBase64(result.base64, result.filename, result.mimeType)}
                    >
                      <i className="fa fa-download me-1"></i>Download again
                    </button>
                  </div>
                </div>

                {s.details && s.details.length > 0 && (
                  <details open style={{ marginBottom: 8 }}>
                    <summary style={{ cursor: "pointer", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                      Per-lane result ({s.details.length})
                    </summary>
                    <div style={{ overflowX: "auto", maxHeight: 420, overflowY: "auto" }}>
                      <table className="table table-sm table-hover" style={{ fontSize: 13 }}>
                        <thead>
                          <tr style={{ fontSize: 11, textTransform: "uppercase", color: "#7f8c8d" }}>
                            <th>Lane</th>
                            <th className="text-end">Lowest Rate</th>
                            <th className="text-end">Target Rate</th>
                            <th className="text-end">Highest Rate</th>
                            <th className="text-end">Rows</th>
                            <th>Source</th>
                          </tr>
                        </thead>
                        <tbody>
                          {s.details.map((d, i) => (
                            <tr key={i}>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {d.origin} → {d.destination}
                              </td>
                              <td className="text-end">{money(d.rate_low)}</td>
                              <td className="text-end" style={{ fontWeight: 600 }}>{money(d.rate)}</td>
                              <td className="text-end">{money(d.rate_high)}</td>
                              <td className="text-end">{d.rows}</td>
                              <td>{sourceLabel(d.source)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout2>
  );
}
